import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectSpread from '@babel/runtime/helpers/objectSpread';
import warning from 'tiny-warning';
import isEqual from 'lodash.isequal';
import { initialize } from 'ldclient-js';
import camelCase from 'lodash.camelcase';

var adapterState = {
  isReady: false,
  isConfigured: false,
  user: null,
  client: null,
  flags: null
};

var updateFlagsInAdapterState = function updateFlagsInAdapterState(updatedFlags) {
  adapterState.flags = _objectSpread({}, adapterState.flags, updatedFlags);
};

var getFlag = function getFlag(flagName) {
  return adapterState.flags && adapterState.flags[flagName];
};

var getIsReady = function getIsReady() {
  return adapterState.isReady;
};

var normalizeFlag = function normalizeFlag(flagName, flagValue) {
  return [camelCase(flagName), // Multi variate flags contain a string or `null` - `false` seems more natural.
  flagValue === null || flagValue === undefined ? false : flagValue];
};

var setupFlagSubcription = function setupFlagSubcription(_ref) {
  var flagsFromSdk = _ref.flagsFromSdk,
      onFlagsStateChange = _ref.onFlagsStateChange;

  var _loop = function _loop(_flagName) {
    // Dispatch whenever a configured flag value changes
    if (Object.prototype.hasOwnProperty.call(flagsFromSdk, _flagName) && adapterState.client) {
      adapterState.client.on("change:".concat(_flagName), function (flagValue) {
        var _normalizeFlag = normalizeFlag(_flagName, flagValue),
            _normalizeFlag2 = _slicedToArray(_normalizeFlag, 2),
            normalizedFlagName = _normalizeFlag2[0],
            normalizedFlagValue = _normalizeFlag2[1];

        var flag = _defineProperty({}, normalizedFlagName, normalizedFlagValue);

        updateFlagsInAdapterState(flag);
        onFlagsStateChange(flag);
      });
    }
  };

  for (var _flagName in flagsFromSdk) {
    _loop(_flagName);
  }
}; // NOTE: Exported for testing only


var createAnonymousUserKey = function createAnonymousUserKey() {
  return Math.random().toString(36).substring(2);
};

var ensureUser = function ensureUser(user) {
  return _objectSpread({
    key: user && user.key ? user.key : createAnonymousUserKey()
  }, user);
};

var initializeClient = function initializeClient(clientSideId, user, clientOptions) {
  return initialize(clientSideId, user);
};

var changeUserContext = function changeUserContext(nextUser) {
  return adapterState.client && adapterState.client.identify ? adapterState.client.identify(nextUser) : Promise.reject(new Error('Can not change user context: client not yet initialized.'));
};

var updateUserContext = function updateUserContext(updatedUserProps) {
  var isAdapterReady = adapterState.isConfigured && adapterState.isReady;
  warning(isAdapterReady, '@flopflip/launchdarkly-adapter: adapter not ready and configured. User context can not be updated before.');
  if (!isAdapterReady) return Promise.reject(new Error('Can not update user context: adapter not yet ready.'));
  return changeUserContext(_objectSpread({}, adapterState.user, updatedUserProps));
}; // NOTE: Exported for testing only


var camelCaseFlags = function camelCaseFlags(rawFlags) {
  return Object.entries(rawFlags).reduce(function (camelCasedFlags, _ref2) {
    var _ref3 = _slicedToArray(_ref2, 2),
        flagName = _ref3[0],
        flagValue = _ref3[1];

    var _normalizeFlag3 = normalizeFlag(flagName, flagValue),
        _normalizeFlag4 = _slicedToArray(_normalizeFlag3, 2),
        normalizedFlagName = _normalizeFlag4[0],
        normalizedFlagValue = _normalizeFlag4[1]; // Can't return expression as it is the assigned value


    camelCasedFlags[normalizedFlagName] = normalizedFlagValue;
    return camelCasedFlags;
  }, {});
};

var getInitialFlags = function getInitialFlags(_ref4) {
  var onFlagsStateChange = _ref4.onFlagsStateChange,
      onStatusStateChange = _ref4.onStatusStateChange;

  if (adapterState.client) {
    return adapterState.client.waitUntilReady().then(function () {
      var flagsFromSdk = adapterState.client ? adapterState.client.allFlags() : null; // First update internal state

      adapterState.isReady = true; // ...to then signal that the adapter is ready

      onStatusStateChange({
        isReady: true
      });

      if (flagsFromSdk) {
        var flags = camelCaseFlags(flagsFromSdk);
        updateFlagsInAdapterState(flags); // ...and flush initial state of flags

        onFlagsStateChange(flags);
        return Promise.resolve({
          flagsFromSdk: flagsFromSdk
        });
      }
    });
  } else {
    return Promise.reject(new Error('Can not subscribte with non initialized client.'));
  }
};

var configure = function configure(_ref5) {
  var clientSideId = _ref5.clientSideId,
      user = _ref5.user,
      _ref5$clientOptions = _ref5.clientOptions,
      clientOptions = _ref5$clientOptions === void 0 ? {} : _ref5$clientOptions,
      onFlagsStateChange = _ref5.onFlagsStateChange,
      onStatusStateChange = _ref5.onStatusStateChange,
      _ref5$subscribeToFlag = _ref5.subscribeToFlagChanges,
      subscribeToFlagChanges = _ref5$subscribeToFlag === void 0 ? true : _ref5$subscribeToFlag;
  adapterState.user = ensureUser(user);
  adapterState.client = initializeClient(clientSideId, adapterState.user, clientOptions);
  adapterState.isConfigured = true;
  return getInitialFlags({
    onFlagsStateChange: onFlagsStateChange,
    onStatusStateChange: onStatusStateChange
  }).then(function (_ref6) {
    var flagsFromSdk = _ref6.flagsFromSdk;
    if (subscribeToFlagChanges) setupFlagSubcription({
      flagsFromSdk: flagsFromSdk,
      onFlagsStateChange: onFlagsStateChange
    });
    return adapterState.client;
  });
};

var reconfigure = function reconfigure(_ref7) {
  var nextUser = _ref7.user;
  if (!adapterState.isConfigured) return Promise.reject(new Error('@flopflip/launchdarkly-adapter: please configure adapter before reconfiguring.'));

  if (!isEqual(adapterState.user, nextUser)) {
    adapterState.user = ensureUser(nextUser);
    return changeUserContext(adapterState.user);
  }

  return Promise.resolve();
};

var adapter = {
  configure: configure,
  reconfigure: reconfigure,
  getFlag: getFlag,
  getIsReady: getIsReady,
  updateUserContext: updateUserContext
};

var version = "2.4.5";

export default adapter;
export { version };
//# sourceMappingURL=@flopflip-launchdarkly-adapter.es.js.map



//////////////////
// WEBPACK FOOTER
// ./~/@flopflip/launchdarkly-adapter/dist/@flopflip-launchdarkly-adapter.es.js
// module id = 410
// module chunks = 1